import { mediaQueryLarge, requestIdleCallback } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Set the layout instantly — no View Transition. The transition snapshotted and morphed every
    // card at once (2 columns -> 1 and back); on a phone that is dozens of cards resizing on a weak
    // CPU, which stuttered badly. The density switch is a discrete toggle and reads fine snapping,
    // and this control only exists on mobile anyway.
    this.#setLayout(value);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);

    // Одноколоночный режим удваивает ширину карточки (191 -> 390 css px), значит и
    // картинке нужен вдвое больший кандидат. Liquid отдаёт sizes для сетки по умолчанию
    // (мобильная доля 50vw), здесь последнее правило поднимается до 100vw. Десктопные
    // брейкпоинты не трогаются. Уже загруженные картинки браузер заново не запрашивает —
    // новый размер получат те, что грузятся лениже ниже экрана; ради этого не форсируем
    // повторную закачку уже показанных. Близнец этой функции — в
    // assets/r69-product-grid-inline.js (там она нужна при восстановлении из сессии).
    const single = value === 'mobile-single';
    for (const img of grid.querySelectorAll('img[sizes]')) {
      if (!img.dataset.r69Sizes) img.dataset.r69Sizes = img.getAttribute('sizes') || '';
      const base = img.dataset.r69Sizes;
      if (!base) continue;
      const next = single ? base.replace(/,\s*\d+vw\s*$/, ', 100vw') : base;
      if (img.getAttribute('sizes') === next) continue;
      img.setAttribute('sizes', next);
      // Смена sizes сама по себе не заставляет браузер перевыбрать кандидата для уже
      // загруженной картинки. Переприсваивание srcset заставляет. Нужно только при
      // переходе в одну колонку: карточка 191 -> 390 css px, и оставить прежний
      // кандидат значит показать половину нужной плотности на экране DPR 3.
      if (single) img.srcset = img.srcset;
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
